import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'

import Navbar from '../components/navbar'
import NavbarFooter from '../components/navbar/NavbarFooter'

export default function PrivacyPolicyPage() {
    return (
        <>
            <Navbar />
            <section>
                <main>
                    <Container sx={{ mt: 4, maxWidth: 'md' }}>
                        <Typography variant="h3" gutterBottom>Privacy Policy</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Last updated: May 7, 2026
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            CrapApp is a crowd-sourced casino minimum-bet reporting service. This policy explains what
                            personal data we collect, how we use it, and your rights around it. We keep things simple
                            and we do not sell your data.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>What We Collect</Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            We collect the minimum information needed to run the service:
                        </Typography>
                        <Typography variant="body1" component="ul" sx={{ mb: 2, pl: 3 }}>
                            <li>
                                <strong>Email address and display name</strong> — provided when you register directly
                                or pulled from your Google or Facebook profile when you sign in with OAuth.
                            </li>
                            <li>
                                <strong>OAuth provider ID</strong> — a unique identifier supplied by Google or Facebook
                                that lets us link your account to your social login without storing your social password.
                            </li>
                            <li>
                                <strong>IP address</strong> — recorded only for security purposes: rate limiting login
                                attempts and temporarily locking an account after repeated failures. We do not use IP
                                addresses for analytics or advertising.
                            </li>
                            <li>
                                <strong>Approximate distance from a casino</strong> — when you submit a minimum-bet
                                report, we store how far you were from the casino at that moment. This is kept in audit
                                logs to help us detect fraudulent or implausible reports. We do not store your precise
                                GPS coordinates, and this value is never shown publicly.
                            </li>
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>How We Use It</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Your data is used solely to operate CrapApp:
                        </Typography>
                        <Typography variant="body1" component="ul" sx={{ mb: 2, pl: 3 }}>
                            <li>Authenticating you and keeping your session secure.</li>
                            <li>Displaying your display name alongside your contributions.</li>
                            <li>Preventing abuse, spam, and fraudulent min-bet reports.</li>
                            <li>Awarding and tracking your contribution points.</li>
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            We do not use your data for advertising, profiling, or any purpose unrelated to running
                            the service.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>Cookies</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            We use a single HttpOnly session cookie to keep you logged in. This cookie contains a
                            short-lived JWT and is essential for the service to function — it is not an analytics or
                            advertising cookie. We do not use any third-party tracking cookies or pixels.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>Data Sharing</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            We do not sell or share your personal data with third parties for commercial purposes.
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            If you sign in with Google or Facebook, those providers authenticate you on our behalf.
                            Their own privacy policies govern how they handle your data during that process:
                        </Typography>
                        <Typography variant="body1" component="ul" sx={{ mb: 2, pl: 3 }}>
                            <li>Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
                            <li>Facebook Privacy Policy: <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">facebook.com/privacy/policy</a></li>
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Beyond the OAuth handshake, we do not send your personal data to Google, Facebook, or any
                            other third party.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>Data Retention &amp; Account Deletion</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            When you delete your account, all personal data tied to your identity — your email address,
                            display name, OAuth provider ID, and IP address logs — is permanently deleted from our
                            systems immediately.
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            The minimum-bet reports you submitted are part of the crowd-sourced dataset that other
                            users rely on. After account deletion those reports are kept but fully anonymized — they
                            are disassociated from your identity and cannot be traced back to you.
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            You can delete your account at any time from the Profile section of the app.
                            You may also request deletion by emailing{' '}
                            <a href="mailto:admin@crapapp.live">admin@crapapp.live</a> — we will process the request
                            within 30 days.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>Contact Us</Typography>
                        <Typography variant="body1" sx={{ mb: 4 }}>
                            Questions or concerns about this policy? Email us at{' '}
                            <a href="mailto:admin@crapapp.live">admin@crapapp.live</a>.
                        </Typography>
                    </Container>
                </main>
            </section>
            <NavbarFooter />
        </>
    )
}
